import { useAnalyticsSkuViewedContext } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed/Context';
import { useDriverTooltipPageAction } from '@cc-shopping/platform-analytics/hooks/useDriverTooltipPageAction';
import { InfoTooltip } from '@cc-shopping/ui/composites/InfoTooltip';
import { theme } from '@cc-shopping/ui/theme';
import { isAmexOffer } from '@cc-shopping/utils/offers';
import { Box, Grid, type GridProps, Typography, useMediaQuery } from '@mui/material';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { useReviewContext } from 'src/app/credit-cards/reviews/components/Context';
import { useApprovalLikelihoodContext } from 'src/features/approval-odds/context';
import { getCardsApprovalOddsDriver } from 'src/features/approval-odds/getCardsApprovalOddsDriver';
import { v4 } from 'uuid';
import { RecommendedCreditScore } from './RecommendedCreditScore';
type ReviewProductCardDetailsProps = {
  vertical?: boolean;
};
export default function ReviewProductCardDetails({
  vertical
}: ReviewProductCardDetailsProps) {
  const {
    marketplaceEntity: {
      details: {
        introOfferDetails,
        rewardRateDetails
      },
      signUpBonusDetailsAnnotation,
      driverAnnualFee,
      driverIntroApr,
      driverIntroOffer,
      driverRegularApr,
      driverRewardRate,
      limitedCreditHistory,
      minFico,
      maxFico,
      product: {
        id: productId
      },
      institution
    }
  } = useReviewContext();
  const isAmexCard = isAmexOffer({
    institution
  });
  const uuidRef = useRef(v4());
  const {
    skuImpressionId
  } = useAnalyticsSkuViewedContext();
  const handleTooltipClickPageAction = useDriverTooltipPageAction('review_product_card');
  const analytics = {
    activityId: uuidRef.current,
    parentActivityId: skuImpressionId,
    sectionName: 'review_product_card'
  };
  const {
    isLoading,
    approvalLikelihoods
  } = useApprovalLikelihoodContext();
  const approvalOdds = approvalLikelihoods ? approvalLikelihoods[productId] : undefined;
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const rcsOrApprovalOdds = approvalOdds ? {
    id: 'approvalOdds',
    ...getCardsApprovalOddsDriver(approvalOdds.approvalLikelihood, isLoading, isDesktop),
    description: undefined
  } : {
    id: 'creditScore',
    label: isAmexCard ? 'Recommended credit' : 'Recommended credit score',
    value: <RecommendedCreditScore odds={{
      min: minFico,
      max: maxFico
    }} hasLimitedCreditHistory={limitedCreditHistory} analytics={analytics} firePageActionOnClick hideTooltip isAmexOffer={isAmexCard} />,
    labelTooltip: null
  };
  const detailColumns: {
    id: string;
    label: string;
    columnSpan: number;
    containerProps?: GridProps;
    description?: string;
    labelTooltip?: ReactNode;
    value?: ReactNode;
    valueTooltip?: string;
  }[] = [{
    id: 'annualFee',
    label: 'Annual Fee',
    value: driverAnnualFee.value,
    columnSpan: vertical ? 6 : 4
  }, {
    description: rewardRateDetails?.rewardTypeDescription,
    id: 'rewardRate',
    label: 'Rewards Rate',
    value: driverRewardRate?.value,
    valueTooltip: rewardRateDetails?.tooltipString,
    columnSpan: vertical ? 6 : 4
  }, {
    description: signUpBonusDetailsAnnotation ? undefined : driverIntroOffer?.description,
    id: 'introOffer',
    label: 'Intro Offer',
    value: driverIntroOffer?.value,
    valueTooltip: introOfferDetails?.tooltipString,
    columnSpan: vertical ? 6 : 4
  }, {
    ...rcsOrApprovalOdds,
    containerProps: {
      display: {
        xs: 'flex',
        md: 'none'
      }
    },
    columnSpan: 6
  }, {
    id: 'introAPR',
    label: 'Intro APR',
    value: driverIntroApr?.value,
    columnSpan: vertical ? 12 : 8
  }, {
    id: 'regularAPR',
    label: 'Regular APR',
    value: driverRegularApr?.value,
    columnSpan: vertical ? 12 : 4
  }, {
    ...rcsOrApprovalOdds,
    containerProps: {
      display: {
        xs: 'none',
        md: 'flex'
      }
    },
    columnSpan: 12
  }];
  return <Grid container sx={{
    '--column-gap': '1rem',
    margin: '0 calc(var(--column-gap) / -2)',
    overflow: 'hidden',
    width: {
      xs: 'calc(100% + var(--column-gap))',
      md: '100%'
    }
  }}>
      {detailColumns.map(({
      columnSpan,
      containerProps,
      description,
      id,
      label,
      labelTooltip,
      value,
      valueTooltip
    }, index) => {
      if (!value) {
        return null;
      }
      const isApprovalOdds = id === 'approvalOdds';
      const driverColumn = <Grid key={`product-card-driver-${id}-${index}`} display="flex" flexDirection="column" pt={{
        xs: '10px',
        md: 3
      }} paddingX="calc(var(--column-gap) / 2)" {...containerProps} size={columnSpan}>
              <Box {...isApprovalOdds && {
          sx: {
            display: 'flex',
            flexDirection: 'row',
            gap: 1
          }
        }}>
                <Typography variant="textBold" sx={{
            mb: 2
          }} data-testid="btmLineCardDriver">
                  {label}
                </Typography>
                {isApprovalOdds && <InfoTooltip text={labelTooltip} ariaLabel={`More information about ${label}`} />}
              </Box>
              <Box width="100%">
                {typeof value === 'string' ? <>
                    <Typography variant="text" sx={{
              pr: 1
            }}>
                      {value}
                    </Typography>
                    {valueTooltip && <Box sx={{
              display: 'inline',
              position: {
                sm: 'absolute'
              }
            }}>
                        <InfoTooltip text={valueTooltip} ariaLabel={`${label} details`} onOpen={() => handleTooltipClickPageAction(label, 'open')} onClose={() => handleTooltipClickPageAction(label, 'close')} onBlur={() => handleTooltipClickPageAction(label, 'close')} />
                      </Box>}
                  </> : <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end'
          }}>
                    <Box sx={{
              display: {
                xs: 'flex',
                md: 'block'
              },
              pr: 1
            }}>
                      {value}
                    </Box>
                    {valueTooltip && <Box sx={{
              display: 'inline-block'
            }}>
                        <InfoTooltip text={valueTooltip} ariaLabel={`${label} details`} onOpen={() => handleTooltipClickPageAction(label, 'open')} onClose={() => handleTooltipClickPageAction(label, 'close')} onBlur={() => handleTooltipClickPageAction(label, 'close')} />
                      </Box>}
                  </Box>}
              </Box>
              {description && <Typography variant="textSmall" textTransform="capitalize" sx={theme => ({
          color: theme.palette.grey[500],
          paddingBottom: 0
        })}>
                  {description === 'cash' ? 'cashback' : description}
                </Typography>}
            </Grid>;
      return driverColumn;
    })}
    </Grid>;
}